import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Bot,
  Calendar,
  CheckCircle,
  Clock,
  Users,
  TrendingUp,
  Briefcase,
  Target,
  DollarSign,
  Zap,
  Award,
  Code,
  Cpu,
  FileSearch,
  Search,
} from "lucide-react";
import { Navigation } from "./Navigation";
import AssessmentCard from "./AssessmentCard";
import SEO, { generateFAQSchema, generateLocalBusinessSchema } from "./SEO";
// Removed EmailJS import - now using Netlify Forms
const BlogTimeline = React.lazy(() => import("./BlogTimeline"));
import SearchBox from "./SearchBox";
import AIMythsAccordion from "./AIMythsAccordion";
import Footer from "./Footer";
import OptimizedImage from "./OptimizedImage";

interface FractionalPMPageProps {
  onCalendarClick: () => void;
  onContactClick: () => void;
}

export default function FractionalPMPage({
  onCalendarClick,
  onContactClick,
}: FractionalPMPageProps) {
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Memoize FAQ data to prevent recreation on every render
  const faqData = useMemo(
    () => [
      {
        question: "How much does fractional project management cost?",
        answer:
          "Fractional project management costs vary based on your specific needs and project scope. Our flexible pricing model allows you to pay only for the time and expertise you need, typically 40-60% less than hiring a full-time project manager. Contact us at hello@one80labs.com or +1-402-915-3616 for a custom quote based on your project requirements.",
      },
      {
        question:
          "What is the difference between fractional and full-time project management?",
        answer:
          "Fractional project management provides experienced project leadership on a part-time, flexible basis, while full-time project management requires hiring a permanent employee. Fractional PM offers cost savings of 40-60%, no benefits or overhead costs, flexible engagement terms, and access to senior-level expertise without the commitment of a full-time hire. It's ideal for growing companies with varying project demands.",
      },
      {
        question:
          "Where does One80Labs provide fractional project management services?",
        answer:
          "One80Labs provides fractional project management services throughout the Midwest region, including Omaha, Nebraska, Lincoln, Council Bluffs, Iowa, Des Moines, Kansas City, and surrounding areas. We offer both in-person services in Omaha and virtual project management support for clients throughout the Midwest region.",
      },
      {
        question:
          "How do I get started with One80Labs fractional project management?",
        answer:
          "To get started with One80Labs fractional project management, contact us at hello@one80labs.com or +1-402-915-3616 to schedule a consultation. We'll discuss your project needs, timeline, and budget to create a customized engagement plan. Our onboarding process ensures seamless integration with your existing team and workflows.",
      },
      {
        question:
          "What industries does One80Labs serve for fractional project management?",
        answer:
          "One80Labs serves a wide range of industries for fractional project management, including technology companies, healthcare organizations, manufacturing, financial services, retail, and non-profit organizations. We have experience managing software development, digital transformation, business process improvement, product launches, technology implementation, and organizational change management projects.",
      },
      {
        question: "What project management methodologies does One80Labs use?",
        answer:
          "One80Labs uses Agile, Scrum, Kanban, and traditional Waterfall methodologies depending on your project needs and industry requirements. Our fractional project managers are certified in multiple methodologies and can adapt their approach to your organization's culture and project complexity. We help implement the most effective methodology for your specific situation.",
      },
      {
        question:
          "How does fractional project management work with remote teams?",
        answer:
          "Fractional project management works exceptionally well with remote teams through virtual collaboration tools, regular video meetings, and digital project management platforms. Our fractional PMs are experienced in managing distributed teams and can help implement effective communication strategies, remote work processes, and team collaboration tools to ensure project success regardless of location.",
      },
      {
        question:
          "What size companies benefit most from fractional project management?",
        answer:
          "Companies with 10-100 employees benefit most from fractional project management, particularly growing businesses that need project expertise but aren't ready for a full-time hire. This includes startups, mid-size companies, and organizations with fluctuating project demands. Our fractional PMs can scale their involvement up or down based on your project needs.",
      },
      {
        question:
          "How do you measure success in fractional project management?",
        answer:
          "We measure success through key performance indicators including project completion rates, timeline adherence, budget management, stakeholder satisfaction, and team productivity improvements. Our fractional project managers provide regular progress reports and help establish measurable goals for each project. We typically see 90%+ project success rates and 25-40% productivity improvements.",
      },
      {
        question:
          "How can fractional project management help healthcare organizations?",
        answer:
          "Fractional project management helps healthcare organizations implement new systems, manage regulatory compliance projects, optimize patient care processes, and lead digital transformation initiatives. Our healthcare PMs understand HIPAA requirements, medical workflows, and healthcare technology implementation. We help healthcare organizations improve efficiency while maintaining compliance and patient care quality.",
      },
      {
        question:
          "How can fractional project management benefit manufacturing companies?",
        answer:
          "Fractional project management benefits manufacturing companies by managing lean implementation, quality improvement initiatives, supply chain optimization, and technology upgrades. Our manufacturing PMs understand production processes, quality systems, and operational excellence methodologies. We help manufacturers reduce costs, improve quality, and increase operational efficiency.",
      },
      {
        question:
          "How can fractional project management help technology startups?",
        answer:
          "Fractional project management helps technology startups by managing product development cycles, fundraising preparation, team scaling, and market expansion initiatives. Our startup PMs understand the unique challenges of fast-growing companies and can help implement scalable processes, manage investor relationships, and coordinate cross-functional teams effectively.",
      },
      {
        question: "What tools and software do fractional project managers use?",
        answer:
          "Our fractional project managers use industry-standard tools including Microsoft Project, Asana, Trello, Jira, Monday.com, and Slack for project management and team collaboration. We can work with your existing tools or recommend the best solutions for your project needs. Our PMs are proficient in multiple platforms and can adapt to your preferred technology stack.",
      },
    ],
    [],
  );

  // Memoize schema objects using proper generation functions
  const faqSchema = useMemo(() => generateFAQSchema(faqData), [faqData]);
  const localBusinessSchema = useMemo(() => generateLocalBusinessSchema(), []);

  // Memoize the combined schemas array
  const memoizedSchemas = useMemo(
    () => [faqSchema, localBusinessSchema],
    [faqSchema, localBusinessSchema],
  );

  return (
    <>
      <SEO
        title="Fractional Project Management Services in Omaha & Midwest | One80Labs"
        description="Transform your project delivery with expert Fractional Project Management services in Omaha and across the Midwest. Get senior-level project leadership without the overhead of a full-time hire."
        image="https://images.unsplash.com/photo-1552664730-d307ca884978"
        jsonLd={memoizedSchemas}
        keywords={[
          "fractional project management Omaha",
          "part-time project manager Nebraska",
          "project management services Midwest",
          "IT project management Omaha",
          "agile project management Nebraska",
          "remote project manager Midwest",
          "project consulting Omaha",
          "technology project management Nebraska",
          "digital transformation Omaha",
          "AI project management Midwest",
          "software project manager Nebraska",
          "project management consultant Omaha",
          "interim project manager Nebraska",
          "project management outsourcing Midwest",
          "project management near me",
        ]}
        // Using Netlify Forms - no onSubmit prop needed
      />
      <div className="min-h-screen bg-white">
        <Navigation onSearchClick={() => setShowSearch(!showSearch)} />
        {showSearch && (
          <div className="max-w-3xl mx-auto px-4 py-3">
            <SearchBox />
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Hero Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm mb-12">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-1/2">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">
                  Expert Project Leadership Without the Full-Time Commitment
                </h1>
                <p className="text-gray-600 mb-6">
                  Our Fractional Project Management services provide the
                  expertise you need, exactly when you need it. We help
                  organizations of all sizes deliver successful projects without
                  the overhead of a full-time hire.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={onCalendarClick}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-black hover:bg-gray-800 transition-colors duration-200"
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Schedule a Consultation
                  </button>
                </div>
              </div>
              <div className="md:w-1/2">
                <OptimizedImage
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2940&q=80"
                  alt="Team collaborating on project"
                  width={1470}
                  height={256}
                  hero={true}
                  className="w-full h-64 object-cover rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* What is Fractional PM */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              What is Fractional Project Management?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <p className="text-gray-600 mb-4">
                  Fractional Project Management provides your organization with
                  experienced project leadership on a part-time, flexible basis.
                  It's the perfect solution for businesses that:
                </p>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-gray-900 mr-2 flex-shrink-0 mt-0.5" />
                    <span>
                      Need project expertise but can't justify a full-time hire
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-gray-900 mr-2 flex-shrink-0 mt-0.5" />
                    <span>
                      Have fluctuating project workloads throughout the year
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-gray-900 mr-2 flex-shrink-0 mt-0.5" />
                    <span>
                      Want to improve project outcomes without increasing
                      headcount
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-gray-900 mr-2 flex-shrink-0 mt-0.5" />
                    <span>
                      Need specialized expertise for specific initiatives
                    </span>
                  </li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <h4 className="text-lg font-medium text-gray-900 mb-4">
                  The Fractional Advantage
                </h4>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="bg-gray-200 p-2 rounded-full mr-4">
                      <Clock className="h-5 w-5 text-gray-700" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">
                        Flexible Engagement
                      </h5>
                      <p className="text-gray-600 text-sm">
                        Scale hours up or down based on your project needs
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-gray-200 p-2 rounded-full mr-4">
                      <TrendingUp className="h-5 w-5 text-gray-700" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">
                        Cost-Effective
                      </h5>
                      <p className="text-gray-600 text-sm">
                        Pay only for the time and expertise you need
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-gray-200 p-2 rounded-full mr-4">
                      <Users className="h-5 w-5 text-gray-700" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">
                        Experienced Talent
                      </h5>
                      <p className="text-gray-600 text-sm">
                        Access to senior-level project managers
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Why Fractional Project Management */}
          <div className="mb-16 bg-gray-50 p-8 rounded-lg border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Why Fractional Project Management?
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="bg-black rounded-full p-3 mr-4">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold">Cost Efficiency</h3>
                </div>
                <p className="text-gray-600">
                  Save 40-60% compared to a full-time hire while still getting
                  senior-level expertise. No benefits, overhead, or downtime
                  costs.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="bg-black rounded-full p-3 mr-4">
                    <Zap className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold">Immediate Impact</h3>
                </div>
                <p className="text-gray-600">
                  Experienced professionals hit the ground running with proven
                  methodologies and best practices, delivering value from day
                  one.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="bg-black rounded-full p-3 mr-4">
                    <Award className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold">Specialized Skills</h3>
                </div>
                <p className="text-gray-600">
                  Access to industry-specific expertise and specialized skills
                  that might be difficult to find in a single full-time hire.
                </p>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Traditional Hiring vs. Fractional PM
                </h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-3 pb-2 border-b border-gray-100">
                    <span className="text-gray-600 font-medium text-left">
                      Traditional Hiring
                    </span>
                    <span className="text-gray-600 font-medium text-center">
                      vs.
                    </span>
                    <span className="text-gray-600 font-medium text-right">
                      Fractional PM
                    </span>
                  </div>
                  <div className="grid grid-cols-3 pb-2 border-b border-gray-100">
                    <span className="text-gray-500 text-left">3-6 months</span>
                    <span className="text-gray-600 font-medium text-center">
                      Hiring Timeline
                    </span>
                    <span className="text-gray-900 font-semibold text-right">
                      1-2 weeks
                    </span>
                  </div>
                  <div className="grid grid-cols-3 pb-2 border-b border-gray-100">
                    <span className="text-gray-500 text-left">$120-180k+</span>
                    <span className="text-gray-600 font-medium text-center">
                      Annual Cost
                    </span>
                    <span className="text-gray-900 font-semibold text-right">
                      $$ variable
                    </span>
                  </div>
                  <div className="grid grid-cols-3 pb-2 border-b border-gray-100">
                    <span className="text-gray-500 text-left">Long-term</span>
                    <span className="text-gray-600 font-medium text-center">
                      Commitment
                    </span>
                    <span className="text-gray-900 font-semibold text-right">
                      Flexible
                    </span>
                  </div>
                  <div className="grid grid-cols-3">
                    <span className="text-gray-500 text-left">Fixed</span>
                    <span className="text-gray-600 font-medium text-center">
                      Scalability
                    </span>
                    <span className="text-gray-900 font-semibold text-right">
                      Adjustable
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Ideal For
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-black mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">
                      Growing companies with fluctuating project demands
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-black mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">
                      Organizations undergoing digital transformation
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-black mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">
                      Startups needing enterprise-level project expertise
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-black mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">
                      Companies with seasonal or cyclical project needs
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-black mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">
                      Businesses looking to optimize project delivery without
                      increasing headcount
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Assessment Card */}
          <div className="mb-12">
            <AssessmentCard onContactClick={onContactClick} />
          </div>

          {/* Services */}
          <div className="mb-16 bg-gray-50 py-16 px-8 rounded-lg border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Our Services
            </h2>

            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="bg-gray-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Briefcase className="h-6 w-6 text-gray-900" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Project Leadership
                </h3>
                <p className="text-gray-600">
                  End-to-end management of critical projects, from planning to
                  successful delivery.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="bg-gray-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Cpu className="h-6 w-6 text-gray-900" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  AI Agent Development
                </h3>
                <p className="text-gray-600">
                  Custom AI solutions tailored to your business needs, from
                  concept to deployment.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="bg-gray-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Code className="h-6 w-6 text-gray-900" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Software Development
                </h3>
                <p className="text-gray-600">
                  Full-stack development services with modern technologies and
                  best practices.
                </p>
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="bg-gray-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <FileSearch className="h-6 w-6 text-gray-900" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Vendor Selection
                </h3>
                <p className="text-gray-600">
                  Expert guidance in selecting and managing technology vendors
                  and partners.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="bg-gray-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Target className="h-6 w-6 text-gray-900" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Strategic Planning
                </h3>
                <p className="text-gray-600">
                  Development of project roadmaps, resource plans, and risk
                  management strategies.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="bg-gray-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-gray-900" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Process Improvement
                </h3>
                <p className="text-gray-600">
                  Optimization of project management methodologies and
                  implementation of best practices.
                </p>
              </div>
            </div>
          </div>
          {/* FAQ Section */}
          <div className="mb-16">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              <FractionalPMFAQ />
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-16 bg-white rounded-lg p-8 border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              How It Works
            </h2>
            <div className="relative">
              {/* Timeline line */}
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gray-200"></div>

              {/* Steps */}
              <div className="space-y-12 relative">
                {/* Step 1 */}
                <div className="flex flex-col md:flex-row items-center">
                  <div className="md:w-1/2 md:pr-12 mb-4 md:mb-0 md:text-right">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Initial Consultation
                    </h3>
                    <p className="text-gray-600">
                      We'll discuss your project needs, challenges, and goals to
                      understand how we can best support your organization.
                    </p>
                  </div>
                  <div className="bg-black text-white rounded-full w-10 h-10 flex items-center justify-center z-10 my-4 md:my-0">
                    <span className="font-medium">1</span>
                  </div>
                  <div className="md:w-1/2 md:pl-12">
                    <OptimizedImage
                      src="https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80"
                      alt="Initial consultation"
                      width={687}
                      height={160}
                      className="rounded-lg w-full h-40 object-cover"
                    />
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex flex-col md:flex-row items-center">
                  <div className="md:w-1/2 md:pr-12 mb-4 md:mb-0 order-2 md:order-1">
                    <OptimizedImage
                      src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
                      alt="Customized solution"
                      width={735}
                      height={160}
                      className="rounded-lg w-full h-40 object-cover"
                    />
                  </div>
                  <div className="bg-black text-white rounded-full w-10 h-10 flex items-center justify-center z-10 my-4 md:my-0 order-1 md:order-2">
                    <span className="font-medium">2</span>
                  </div>
                  <div className="md:w-1/2 md:pl-12 order-3 md:text-left">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Customized Solution
                    </h3>
                    <p className="text-gray-600">
                      We'll design a tailored engagement model that aligns with
                      your specific project requirements and budget.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex flex-col md:flex-row items-center">
                  <div className="md:w-1/2 md:pr-12 mb-4 md:mb-0 md:text-right">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Seamless Integration
                    </h3>
                    <p className="text-gray-600">
                      Our experienced project managers integrate with your team,
                      adapting to your culture and processes.
                    </p>
                  </div>
                  <div className="bg-black text-white rounded-full w-10 h-10 flex items-center justify-center z-10 my-4 md:my-0">
                    <span className="font-medium">3</span>
                  </div>
                  <div className="md:w-1/2 md:pl-12">
                    <OptimizedImage
                      src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
                      alt="Team integration"
                      width={735}
                      height={160}
                      className="rounded-lg w-full h-40 object-cover"
                    />
                  </div>
                </div>

                {/* Step 4 */}
                <div className="flex flex-col md:flex-row items-center">
                  <div className="md:w-1/2 md:pr-12 mb-4 md:mb-0 order-2 md:order-1">
                    <OptimizedImage
                      src="https://images.unsplash.com/photo-1531482615713-2afd69097998?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
                      alt="Project delivery"
                      width={735}
                      height={160}
                      className="rounded-lg w-full h-40 object-cover"
                    />
                  </div>
                  <div className="bg-black text-white rounded-full w-10 h-10 flex items-center justify-center z-10 my-4 md:my-0 order-1 md:order-2">
                    <span className="font-medium">4</span>
                  </div>
                  <div className="md:w-1/2 md:pl-12 order-3 md:text-left">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Successful Delivery
                    </h3>
                    <p className="text-gray-600">
                      We drive your projects to successful completion, ensuring
                      all objectives are met and value is delivered.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA - Now with white background */}
          <div className="bg-white rounded-lg p-8 border border-gray-200 shadow-sm">
            <div className="text-center max-w-3xl mx-auto">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Ready to Transform Your Project Delivery?
              </h3>
              <p className="text-gray-600 mb-6">
                Schedule a free consultation to discuss how our Fractional
                Project Management services can help your organization deliver
                successful projects.
              </p>
              <button
                onClick={onCalendarClick}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-black hover:bg-gray-800 transition-colors duration-200"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Schedule a Consultation
              </button>
            </div>
          </div>
        </div>

        {/* Latest Updates Timeline Section */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Blog Timeline
            </h2>
            <React.Suspense
              fallback={
                <div className="w-full h-[300px] bg-gray-200 animate-pulse rounded-lg"></div>
              }
            >
              <BlogTimeline />
            </React.Suspense>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}

function FractionalPMFAQ() {
  const [expandedItem, setExpandedItem] = useState<number | null>(null);

  const faqs = [
    {
      icon: <Briefcase className="h-8 w-8 text-gray-900" />,
      question: "What exactly is Fractional Project Management?",
      answer:
        "Fractional Project Management is a flexible service model where organizations get access to experienced project management expertise on a part-time or project basis. Instead of hiring a full-time project manager, you can leverage our services for exactly the hours and expertise you need. This approach is particularly beneficial for growing companies, startups, or organizations with varying project demands throughout the year.",
      cta: "Get flexible project management that scales with your needs.",
    },
    {
      icon: <Users className="h-8 w-8 text-gray-900" />,
      question:
        "How do you ensure seamless integration with our existing team?",
      answer:
        "We follow a structured onboarding process that includes understanding your team's culture, existing workflows, and communication preferences. Our project managers are experienced in adapting to different organizational environments and can quickly align with your processes while bringing best practices to enhance your project delivery capabilities. We also use your preferred project management tools and communication platforms to ensure smooth collaboration.",
      cta: "Experience seamless integration with your existing team and tools.",
    },
    {
      icon: <FileSearch className="h-8 w-8 text-gray-900" />,
      question: "What types of projects do you typically manage?",
      answer:
        "We have extensive experience managing a wide range of projects across different industries, including: Software development and digital transformation initiatives, Business process improvement and optimization projects, Product launches and go-to-market strategies, Technology implementation and integration projects, and Organizational change management initiatives.",
      cta: "Let us manage your complex projects with proven expertise.",
    },
    {
      icon: <Clock className="h-8 w-8 text-gray-900" />,
      question: "What is the typical engagement duration and time commitment?",
      answer:
        "Our engagements are highly flexible and can be tailored to your specific needs. We offer: Short-term engagements (1-3 months) for specific project initiatives, Medium-term commitments (3-6 months) for larger projects, Long-term partnerships for ongoing project management support, and Time commitments ranging from 10-30 hours per week, adjustable based on project demands.",
      cta: "Choose the engagement model that works best for your timeline.",
    },
    {
      icon: <Target className="h-8 w-8 text-gray-900" />,
      question: "How do you measure and report project success?",
      answer:
        "We implement a comprehensive project monitoring and reporting system that includes: Regular status updates and progress reports, Key performance indicators (KPIs) tracking, Risk and issue management logs, Budget tracking and variance analysis, Milestone achievement reports, and Stakeholder satisfaction surveys. We customize our reporting approach to align with your organization's preferences and requirements.",
      cta: "Get transparent, data-driven insights into your project performance.",
    },
  ];

  const toggleItem = (index: number) => {
    setExpandedItem(expandedItem === index ? null : index);
  };

  return (
    <div className="space-y-4">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="border border-gray-200 rounded-lg overflow-hidden bg-white transition-all duration-300 hover:shadow-md"
        >
          <button
            onClick={() => toggleItem(index)}
            className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-gray-50 transition-colors duration-200"
          >
            <div className="flex items-center space-x-4">
              <div className="bg-gray-100 p-3 rounded-lg">{faq.icon}</div>
              <h3 className="text-lg font-semibold text-gray-900">
                {faq.question}
              </h3>
            </div>
            {expandedItem === index ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5 text-gray-400"
              >
                <path d="m18 15-6-6-6 6" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5 text-gray-400"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            )}
          </button>

          <div
            className={`px-6 overflow-hidden transition-all duration-300 ${
              expandedItem === index ? "max-h-96 py-4" : "max-h-0"
            }`}
          >
            <p className="text-gray-600 mb-4">{faq.answer}</p>
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-[#F98B3D] mt-1 mr-2 flex-shrink-0" />
              <p className="text-[#F98B3D]">{faq.cta}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
